import React, { useState, useEffect, useCallback } from 'react';
import { Download, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { libraryCacheManager, type CacheProgress, StorageManager } from '../../utils/libraryCache';
import { logger } from '../../utils/logger';
import { createPortal } from 'react-dom';

interface LibraryDownloadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: (success: boolean) => void;
  autoStart?: boolean;
}

const LibraryDownloadDialog: React.FC<LibraryDownloadDialogProps> = ({
  isOpen,
  onClose,
  onComplete,
  autoStart = false,
}) => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState<CacheProgress>({
    status: 'downloading',
    message: '',
    progress: 0,
    total: 0,
    current: 0,
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const [storageInfo, setStorageInfo] = useState<{
    available: string;
    required: string;
    hasSpace: boolean;
  } | null>(null);

  // Check storage space on mount
  useEffect(() => {
    const checkStorage = async () => {
      try {
        const requiredSize = libraryCacheManager.getTotalDownloadSize();
        const hasSpace = await StorageManager.hasEnoughSpace(requiredSize);
        const { quota, usage } = await StorageManager.getStorageEstimate();

        setStorageInfo({
          available: StorageManager.formatBytes(quota - usage),
          required: StorageManager.formatBytes(requiredSize),
          hasSpace,
        });
      } catch (error) {
        logger.error('Failed to check storage:', error);
      }
    };

    if (isOpen) {
      checkStorage();
    }
  }, [isOpen]);

  const handleStartDownload = useCallback(async () => {
    if (!storageInfo?.hasSpace) {
      setProgress({
        status: 'error',
        message: t('insufficient_storage'),
        progress: 0,
        total: 0,
        current: 0,
      });
      return;
    }

    setIsDownloading(true);
    try {
      await libraryCacheManager.downloadEssentialLibraries();
    } catch (error) {
      logger.error('Download failed:', error);
      setIsDownloading(false);
    }
  }, [storageInfo?.hasSpace, t]);

  // Auto start download if enabled
  useEffect(() => {
    if (isOpen && autoStart && !isDownloading && storageInfo?.hasSpace) {
      handleStartDownload();
    }
  }, [isOpen, autoStart, storageInfo, isDownloading, handleStartDownload]);

  // Setup progress listener
  useEffect(() => {
    if (!isOpen) return;

    const unsubscribe = libraryCacheManager.onDownloadProgress((progressData) => {
      setProgress(progressData);

      if (progressData.status === 'completed' || progressData.status === 'error') {
        setIsDownloading(false);
        if (onComplete) {
          onComplete(progressData.status === 'completed');
        }
      }
    });

    return unsubscribe;
  }, [isOpen, onComplete]);

  const handleClose = () => {
    if (!isDownloading) {
      onClose();
    }
  };

  const handleSkip = () => {
    if (onComplete) {
      onComplete(false);
    }
    onClose();
  };

  if (!isOpen) return null;

  const getStatusIcon = () => {
    switch (progress.status) {
      case 'downloading':
        return <Loader2 className="w-6 h-6 text-red-600 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-6 h-6 text-red-500" />;
      default:
        return <Download className="w-6 h-6 text-red-600" />;
    }
  };

  const getProgressBarColor = () => {
    switch (progress.status) {
      case 'downloading':
        return 'bg-red-600';
      case 'completed':
        return 'bg-green-500';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-red-600';
    }
  };

  const dialogContent = (
    <div className="fixed inset-0 bg-black/50 bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <h2 className="text-lg font-semibold text-red-600">{t('library_download_title')}</h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-center justify-center mb-4">{getStatusIcon()}</div>
          {/* Storage Information */}
          {storageInfo && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-red-600 mb-2">{t('storage_information')}</h3>
              <div className="space-y-1 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>{t('required_space')}:</span>
                  <span className="font-medium">{storageInfo.required}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('available_space')}:</span>
                  <span className="font-medium">{storageInfo.available}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('storage_status')}:</span>
                  <span
                    className={`font-medium ${storageInfo.hasSpace ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {storageInfo.hasSpace ? t('sufficient') : t('insufficient')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Progress Message */}
          <div className="mb-4">
            <p className="text-gray-700 text-sm leading-relaxed">
              {progress.message || t('library_download_message')}
            </p>
          </div>

          {/* Progress Bar */}
          {(isDownloading || progress.status === 'completed') && (
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>{t('download_progress')}</span>
                <span>
                  {progress.current}/{progress.total}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${getProgressBarColor()}`}
                  style={{ width: `${progress.progress}%` }}
                />
              </div>
              <div className="text-xs text-gray-500 mt-1 text-center">
                {Math.round(progress.progress)}%
              </div>
            </div>
          )}

          {/* Status Message */}
          {progress.status === 'error' && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{progress.message}</p>
            </div>
          )}

          {progress.status === 'completed' && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-700 text-sm">{progress.message}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50 flex-shrink-0 rounded-b-lg">
          {!isDownloading && progress.status !== 'completed' && (
            <>
              <button
                onClick={handleSkip}
                className="w-full px-4 py-2 text-red-600 border border-red-600 rounded-lg hover:bg-red-50 transition-colors"
              >
                {t('skip')}
              </button>
              {storageInfo?.hasSpace && (
                <button
                  onClick={handleStartDownload}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
                >
                  <Download className="w-4 h-4" />
                  <span>{t('download')}</span>
                </button>
              )}
            </>
          )}

          {isDownloading && (
            <div className="flex items-center space-x-2 text-gray-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>{t('downloading')}</span>
            </div>
          )}

          {progress.status === 'completed' && (
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center space-x-2"
            >
              <CheckCircle className="w-4 h-4" />
              <span>{t('done')}</span>
            </button>
          )}

          {progress.status === 'error' && (
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              {t('close')}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(dialogContent, document.body);
};

export default LibraryDownloadDialog;
